// motoCopy.ts
// Centralized strings for Motorcycle Quote Wizard, now JSX-friendly.

import { ReactNode } from "react";

/** Labels/help/titles can be ReactNode; placeholders remain strings */
type LabelNode = ReactNode;

export interface IMotoCopy {
  appTitle: LabelNode;
  sectionHelpHeader: LabelNode;

  buttons: {
    previous: LabelNode;
    next: LabelNode;
    submit: LabelNode;
    wizardView: LabelNode;
    verticalView: LabelNode;
    demoToggle: LabelNode;
    completeBadge: LabelNode;
  };

  personal: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      firstName: { label: LabelNode; placeholder: string };
      lastName: { label: LabelNode; placeholder: string };
      dob: { label: LabelNode };
      email: { label: LabelNode; placeholder: string };
      phone: { label: LabelNode; placeholder: string };
      address1: { label: LabelNode; placeholder: string };
      address2: { label: LabelNode; placeholder: string };
      city: { label: LabelNode };
      state: { label: LabelNode; placeholder: string };
      zip: { label: LabelNode; placeholder: string };
    };
  };

  riderHistory: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      licenseNumber: { label: LabelNode; placeholder: string };
      issuingState: { label: LabelNode; placeholder: string };
      yearsRiding: { label: LabelNode; placeholder: string };
      msfCertified: {
        label: LabelNode;
        placeholder: string;
        options: { yes: LabelNode; no: LabelNode };
      };
      safetyGear: {
        label: LabelNode;
        placeholder: string;
        options: { full: LabelNode; partial: LabelNode; helmetOnly: LabelNode };
      };
      ticketsLast3Yrs: { label: LabelNode; placeholder: string };
      atFaultAccidents: { label: LabelNode; placeholder: string };
    };
  };

  filings: {
    title: LabelNode;
    help: LabelNode;
    needFilingCheckbox: LabelNode;
    fr44NotePrefix: LabelNode;
    fr44NoteStates: LabelNode;
    fields: {
      filingType: {
        label: LabelNode;
        placeholder: string;
        options: { sr22: LabelNode; fr44: LabelNode };
      };
      filingState: { label: LabelNode; placeholder: string };
      filingDuration: { label: LabelNode; placeholder: string };
    };
  };

  motorcycle: {
    title: LabelNode;
    help: LabelNode;
    ownMotoCheckbox: LabelNode;
    vinTip: LabelNode;
    vinInvalid: LabelNode;
    vinLengthPrefix: LabelNode;
    fields: {
      year: { label: LabelNode; placeholder: string };
      make: { label: LabelNode };
      model: { label: LabelNode };
      engineCC: { label: LabelNode; placeholder: string };
      use: {
        label: LabelNode;
        placeholder: string;
        options: { commute: LabelNode; pleasure: LabelNode; business: LabelNode; track: LabelNode };
      };
      vin: { label: LabelNode; placeholder: string };
      annualMileage: { label: LabelNode; placeholder: string };
    };
  };

  coverage: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      coverageType: {
        label: LabelNode;
        placeholder: string;
        options: { liability: LabelNode; full: LabelNode; custom: LabelNode };
      };
      compDeductible: { label: LabelNode; placeholder: string };
    };
  };

  remarks: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      remarks: { label: LabelNode; placeholder: string };
    };
  };

  submitThankYou: {
    header: LabelNode;
    body: LabelNode;
  };
}

export const MOTO_COPY: IMotoCopy = {
  appTitle: <>Motorcycle Quote</>,
  sectionHelpHeader: <>Section Help</>,

  buttons: {
    previous: <>Previous</>,
    next: <>Next</>,
    submit: (
      <>
        <strong>Submit your information</strong>
      </>
    ),
    wizardView: <>Wizard View</>,
    verticalView: <>Vertical View</>,
    demoToggle: <>Demo Mode (auto-fill sample data)</>,
    completeBadge: <>Complete</>,
  },

  personal: {
    title: <>Personal Information</>,
    help: (
      <>

       <span>Provide your legal Name, Date of Birth, eMail, Phone, and Address so we can match records and contact you with your quote. <br></br> <br></br><strong>Please double check all data for accuracy. <i className="bi bi-emoji-smile fs-5 text-success"></i></strong></span>

        {/* 
        Provide your legal name, DOB, contact info, and mailing address so we can reach you with your
        quote. <em>Your info is kept private.</em> 
        */}
      </>
    ),
    fields: {
      firstName: { label: <>First Name</>, placeholder: "Jane" },
      lastName: { label: <>Last Name</>, placeholder: "Doe" },
      dob: { label: <>Date of Birth</> },
      email: { label: <>Email</>, placeholder: "you@example.com" },
      phone: { label: <>Phone Number</>, placeholder: "(555) 555-1234" },
      address1: { label: <>Address Line 1</>, placeholder: "123 Main St" },
      address2: { label: <>Address Line 2 (optional)</>, placeholder: "Apt / Suite / Unit" },
      city: { label: <>City</> },
      state: { label: <>State</>, placeholder: "Select a state…" },
      zip: { label: <>ZIP Code</>, placeholder: "80302" },
    },
  },

  riderHistory: {
    title: <>Rider History</>,
    help: (
      <>
        Tell us about your riding experience and recent history. This helps determine eligibility
        and rates.<br></br> <strong><i>Safer riders often save more!</i></strong>
      </>
    ),
    fields: {
      licenseNumber: { label: <>License Number</>, placeholder: "A123-456-789" },
      issuingState: { label: <>Issuing State</>, placeholder: "Select a state…" },
      yearsRiding: { label: <>Years Riding</>, placeholder: "e.g. 5" },
      msfCertified: {
        label: <>MSF Course Completed</>,
        placeholder: "Select…",
        options: { yes: <>Yes</>, no: <>No</> },
      },
      safetyGear: {
        label: <>Regular Safety Gear</>,
        placeholder: "Select…",
        options: {
          full: <>Helmet + Jacket + Gloves + Boots</>,
          partial: <>Helmet + partial gear</>,
          helmetOnly: <>Helmet only</>,
        },
      },
      ticketsLast3Yrs: { label: <>Tickets (last 3 yrs)</>, placeholder: "e.g. 1 speeding" },
      atFaultAccidents: { label: <>At-Fault Accidents (last 3 yrs)</>, placeholder: "e.g. 0" },
    },
  },

  filings: {
    title: <>DUI: SR-22 / FR-44 Requirements</>,
    help: (
      <>
        <p>If you have a DUI, Some states require SR-22 or FR-44 filings. Enable if needed and choose type, state, and duration.
        If you are not sure, no worries, your agent will help find the right information.</p>
        {/*  <a href="/filings-info">Learn more</a>. */}
      </>
    ),
    needFilingCheckbox: <>I need SR-22 / FR-44</>,
    fr44NotePrefix: <>FR-44 filings are available in </>,
    fr44NoteStates: (
      <>
        <strong>Virginia (VA)</strong> and <strong>Florida (FL)</strong>
      </>
    ),
    fields: {
      filingType: {
        label: <>Filing Type</>,
        placeholder: "Select...",
        options: { sr22: <>SR-22</>, fr44: <>FR-44</> },
      },
      filingState: { label: <>Filing State</>, placeholder: "Select a state…" },
      filingDuration: { label: <>Expected Filing Duration (years)</>, placeholder: "e.g. 3" },
    },
  },

  motorcycle: {
    title: <>Motorcycle</>,
    help: (
      <>
        Provide your bike's details if you own one: year/make/model, optional VIN, engine displacement
        (cc) ( what size is the engine - how many CCs?), use and miles. <br></br><br></br><p><i>The VIN is optional at this point.</i></p>
      </>
    ),
    ownMotoCheckbox: <>I own a motorcycle</>,
    vinTip: (
      <>
        Tip: VINs exclude <code>I</code>, <code>O</code>, and <code>Q</code>. You can usually find
        your VIN on your registration or insurance card.
      </>
    ),
    vinInvalid: <>VINs are exactly 17 characters. Double-check and try again.</>,
    vinLengthPrefix: <>VIN length</>,
    fields: {
      year: { label: <>Year</>, placeholder: "e.g. 2021" },
      make: { label: <>Make</> },
      model: { label: <>Model</> },
      engineCC: { label: <>Engine Displacement (cc)</>, placeholder: "e.g. 649" },
      use: {
        label: <>Primary Use</>,
        placeholder: "Select...",
        options: {
          commute: <>Commute</>,
          pleasure: <>Pleasure</>,
          business: <>Business</>,
          track: <>Track</>,
        },
      },
      vin: { label: <>VIN (optional)</>, placeholder: "Enter 17 characters (optional)" },
      annualMileage: { label: <>Annual Mileage</>, placeholder: "e.g. 5000" },
    },
  },

  coverage: {
    title: <>Coverage (Optional)</>,
    help: <>Choose a coverage preference now or leave blank to discuss with an agent.</>,
    fields: {
      coverageType: {
        label: <>Coverage Type</>,
        placeholder: "Select...",
        options: {
          liability: <>Liability Only</>,
          full: <>Full Coverage</>,
          custom: <>Custom</>,
        },
      },
      compDeductible: { label: <>Comp Deductible</>, placeholder: "e.g. 500" },
    },
  },

  remarks: {
    title: <>Remarks (Optional)</>,
    help: <>Add any notes or preferences for your agent. <br></br>
    For example... when is a good time to call?</>,
    fields: {
      remarks: { label: <>Remarks</>, placeholder: "Anything else we should know?" },
    },
  },

  submitThankYou: {
    header: <>Thank you!</>,
    body: (
      <>
        An insurance agent will reach out to you to review and complete your application.
        <br />
        You can also <a href="/schedule">schedule a call</a> at your convenience.
      </>
    ),
  },
};
